import React, { useEffect, useState, useRef, useMemo } from 'react';
import './index.css';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from './Constants';
import '@fortawesome/fontawesome-free/css/all.css';
import AddJobModal from './components/modals/AddJobModal';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import api from './components/login/interceptors';
import { Button, TableSortLabel, Typography, useTheme } from '@mui/material';
import { CiSearch } from "react-icons/ci";
import { Table, TableHead, TableBody, TableCell, TableRow } from '@mui/material'
import SkillChipsPopover from './components/table/SkillChipPopover';
import JobStatusToggle from './components/modals/candidate/JdStatusToggle';
import { PaginationButtons, RowsPerPageSelector } from './utils/paginationUtils';
import usePlanGate from './hooks/usePlanGate';

const JobDescriptionList = () => {
  // Add Job is limited by the plan's active-job allowance
  const jobGate = usePlanGate('job_slots');
  const [isJDLoading, setIsJDLoading] = useState(false);
  const [originalJobDescriptions, setOriginalJobDescriptions] = useState([]);
  const preventRaceCurrent = useRef(false);
  const firstHeaderCellRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortField, setSortField] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  const tableCellWidth = {
    title: "200px",
    mandatorySkills: "max-content",
    preferredSkills: "max-content",
    candCount: "max-content",
    minExp: "max-content",
    status: "max-content",
  }

  const theme = useTheme();
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const fetchJobDescriptions = async () => {
    setIsJDLoading(true);
    try {
      preventRaceCurrent.current = true;
      const response = await api.get(`${BASE_URL}/api/job-descriptions/`);
      const fetchedDescriptions = response?.data || [];
      const parsedDescriptions = fetchedDescriptions.map(desc => ({
        ...desc,
        jd_min_exp: Math.round(parseFloat(desc.jd_min_exp) || 0),
      }));
      setOriginalJobDescriptions(parsedDescriptions);
    } catch (error) {
      console.error('Error fetching job descriptions:', error);
    } finally {
      setIsJDLoading(false);
    }
  };
  useEffect(() => {
    if (originalJobDescriptions.length === 0) {
      fetchJobDescriptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedJobDescriptionsState = useMemo(() => {
    if (originalJobDescriptions.length === 0) return [];

    let filteredDescriptions = originalJobDescriptions;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredDescriptions = filteredDescriptions.filter((jd) =>
        (jd.title || jd.ats_title || "").toLowerCase().includes(query) ||
        (jd.mandatory_skills || []).join(" ").toLowerCase().includes(query) ||
        (jd.preferred_skills || []).join(" ").toLowerCase().includes(query)
      );
    }

    return [...filteredDescriptions].sort((a, b) => {
      let valueA, valueB;
      switch (sortField) {
        case 'title':
          valueA = (a.title || a.ats_title || 'No Job Title').toLowerCase();
          valueB = (b.title || b.ats_title || 'No Job Title').toLowerCase();
          break;
        case 'mandatory_skills':
          valueA = (a.mandatory_skills || []).length;
          valueB = (b.mandatory_skills || []).length;
          break;
        case 'preferred_skills':
          valueA = (a.preferred_skills || []).length;
          valueB = (b.preferred_skills || []).length;
          break;
        case 'min_exp':
          valueA = a.jd_min_exp || 0;
          valueB = b.jd_min_exp || 0;
          break;
        case 'status':
          valueA = a.status || '';
          valueB = b.status || '';
          break;
        case 'cand_count':
          valueA = a.candidate_count || '';
          valueB = b.candidate_count || '';
          break;
        default:
          return 0;
      }

      if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [originalJobDescriptions, sortField, sortOrder, searchQuery]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(currentPage * rowsPerPage, sortedJobDescriptionsState.length);
  const jobsToDisplay = sortedJobDescriptionsState.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedJobDescriptionsState.length / rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
    // eslint-disable-next-line
  }, [totalPages]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setTimeout(() => {
      if (firstHeaderCellRef.current) {
        firstHeaderCellRef.current.focus();
      }
    }, 0);
  };

  function handleModalOpen() {
    setIsModalOpen(true)
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const error = urlParams.get('error');
    if (error) {
      if (window.opener) {
        window.opener.postMessage({ error: "access_denied" }, window.opener.location.origin);
        window.close();
      }
      return;
    }

    if (authCode && window.opener) {
      window.opener.postMessage({ code: authCode }, window.opener.location.origin);
      window.close();
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <div className="flex flex-col w-full h-full" style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}>
      <div className="w-full flex justify-between items-center gap-2 py-5 px-4 sm:px-6 max-sm:pl-12 border border-l-0 border-r-0 shadow-md"
        style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}>
        <h1 className="font-semibold text-2xl">Jobs</h1>

        <div className="flex gap-2">
          <div className="relative w-full md:w-80">
            <CiSearch
              style={{
                position: 'absolute',
                left: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.palette.text.main,
                pointerEvents: 'none',
                fontSize: '16px',
              }}
            />
            <input
              type="text"
              placeholder={"Search job title or skills"}
              aria-label='Search job title or skills'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`border px-2 py-1 pl-8 text-sm rounded w-full focus:outline-none focus:ring-0 focus:border ${searchQuery ? 'pr-8' : ''}`}
              style={{
                borderColor: theme.palette.secondary.main,
                color: theme.palette.text.main,
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-gray-medium hover:text-gray-700"
                aria-label="Clear search"
              >
                <XMarkIcon className='w-4 h-4' />
              </button>
            )}
          </div>

          <Button
            {...jobGate.controlProps}
            onClick={jobGate.guard(handleModalOpen)}
            sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.primary.main, minWidth: 'max-content' }}
          >
            <span>Add Job</span>
          </Button>
          {jobGate.hint}
        </div>
      </div>

      {isJDLoading ? (
        <div className="mx-auto px-6 sm:px-12 lg:px-16 text-center mt-16">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-2 border-t-4 border-black rounded-full" />
          <p>Loading job descriptions...</p>
        </div>
      ) : (
        <>
          <div className="overflow-auto border rounded-md m-6 max-md:mx-4 max-mt-4 mb-0" style={{ color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}>
            <div className='min-w-full min-h-full'>
              {sortedJobDescriptionsState && sortedJobDescriptionsState.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 text-accent-gray" style={{ borderColor: theme.palette.secondary.v4, background: theme.palette.primary.main }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium mb-1">No job descriptions available</p>
                  <p className="text-sm text-accent-gray max-w-md text-center px-6">
                    Job descriptions you create will appear here. Get started by creating your first job description.
                  </p>
                </div>
              ) : (
                <Table stickyHeader className="w-full min-w-max border-0" sx={{ backgroundColor: theme.palette.primary.main, borderColor: theme.palette.secondary.v4 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        ref={firstHeaderCellRef}
                        tabIndex={0}
                        sx={{
                          paddingLeft: 3,
                          minWidth: '300px',
                          backgroundColor: theme.palette.secondary.v5,
                          color: theme.palette.text.main,
                          zIndex: 10
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "title"}
                          direction={sortField === "title" ? sortOrder : "asc"}
                          onClick={() => handleSort("title")}
                        >
                          Title
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        sx={{
                          width: tableCellWidth.mandatorySkills,
                          paddingLeft: '8px',
                          backgroundColor: theme.palette.secondary.v5,
                          color: theme.palette.text.main,
                          zIndex: 10
                        }}
                      >
                        <TableSortLabel
                          sx={{
                            backgroundColor: theme.palette.secondary.v5,
                            color: theme.palette.text.main,
                            zIndex: 10
                          }}
                          active={sortField === "mandatory_skills"}
                          direction={sortField === "mandatory_skills" ? sortOrder : "asc"}
                          onClick={() => handleSort("mandatory_skills")}
                        >
                          Mandatory Skills
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        sx={{
                          width: tableCellWidth.preferredSkills,
                          paddingLeft: 1,
                          backgroundColor: theme.palette.secondary.v5,
                          color: theme.palette.text.main,
                          zIndex: 10
                        }}
                      >
                        <TableSortLabel
                          sx={{
                            backgroundColor: theme.palette.secondary.v5,
                            color: theme.palette.text.main,
                            zIndex: 10
                          }}
                          active={sortField === "preferred_skills"}
                          direction={sortField === "preferred_skills" ? sortOrder : "asc"}
                          onClick={() => handleSort("preferred_skills")}
                        >
                          Preferred Skills
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        sx={{
                          width: tableCellWidth.candCount,
                          textAlign: 'center',
                          backgroundColor: theme.palette.secondary.v5,
                          color: theme.palette.text.main,
                          zIndex: 10
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "cand_count"}
                          direction={sortField === "cand_count" ? sortOrder : "asc"}
                          onClick={() => handleSort("cand_count")}
                        >
                          Cand. Count
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        sx={{
                          width: tableCellWidth.minExp,
                          textAlign: 'center',
                          backgroundColor: theme.palette.secondary.v5,
                          color: theme.palette.text.main,
                          zIndex: 10
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "min_exp"}
                          direction={sortField === "min_exp" ? sortOrder : "asc"}
                          onClick={() => handleSort("min_exp")}
                        >
                          Min. Exp.
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        sx={{
                          width: tableCellWidth.status,
                          textAlign: 'center',
                          backgroundColor: theme.palette.secondary.v5,
                          color: theme.palette.text.main,
                          zIndex: 10,
                          paddingRight: '10px'
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "status"}
                          direction={sortField === "status" ? sortOrder : "asc"}
                          onClick={() => handleSort("status")}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {jobsToDisplay.map((job, index) => (
                      <TableRow
                        key={index}
                        className="hover:cursor-pointer hover:bg-gray-50 focus:outline focus:outline-2 focus:outline-accent-blue focus:outline-offset-2 rounded-sm"
                        style={{ borderBottom: `1px solid ${theme.palette.secondary.v3}` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/jobs/${job.jd_uuid}`);
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && e.currentTarget === e.target) {
                            e.preventDefault();
                            navigate(`/jobs/${job.jd_uuid}`);
                          }
                        }}
                        tabIndex={0}
                        aria-label={`View details for job: ${job.title || job.ats_title || 'No Job Title'}`}
                      >
                        <TableCell
                          sx={{
                            paddingLeft: 3,
                            width: tableCellWidth.title,
                            maxWidth: tableCellWidth.title,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            textTransform: 'capitalize'
                          }}
                        >
                          <Typography
                            sx={{
                              whiteSpace: 'normal',
                              overflowWrap: 'break-word',
                              wordBreak: 'break-word',
                            }}
                          >
                            {job.title || job.ats_title || 'No Job Title'}
                          </Typography>
                        </TableCell>

                        <SkillChipsPopover
                          width={tableCellWidth.mandatorySkills}
                          skills={job.mandatory_skills}
                        />

                        <SkillChipsPopover
                          width={tableCellWidth.preferredSkills}
                          skills={job.preferred_skills}
                        />

                        <TableCell
                          sx={{ width: tableCellWidth.candCount, textAlign: 'center', paddingRight: '20px' }}
                        >
                          {job.candidate_count || 0}
                        </TableCell>

                        <TableCell
                          sx={{ width: tableCellWidth.minExp, textAlign: 'center', paddingRight: '20px' }}
                        >
                          {job.jd_min_exp || 0}
                        </TableCell>

                        <TableCell
                          sx={{ width: tableCellWidth.status, textAlign: 'center', paddingRight: '10px' }}
                        >
                          <JobStatusToggle job={job} refreshData={fetchJobDescriptions} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          {sortedJobDescriptionsState && sortedJobDescriptionsState.length !== 0 && (
            <div className="mt-auto w-full flex flex-col justify-center items-center px-3 py-3 sm:px-6">
              <div className="relative flex max-lg:flex-col w-full justify-center items-center">
                <RowsPerPageSelector rowsPerPage={rowsPerPage} handleChangeRowsPerPage={handleChangeRowsPerPage} />
                <nav
                  aria-label="Pagination"
                  className="flex flex-wrap justify-center items-center gap-2"
                >
                  <button
                    onClick={() => handlePageChange(currentPage > 1 ? currentPage - 1 : 1)}
                    className={`relative inline-flex items-center border rounded px-2 py-2 text-white-400 bg-white hover:bg-accent-blue hover:text-white z-10 focus:outline-offset-0 text-xs sm:text-sm flex-shrink-0 ${currentPage === 1 || sortedJobDescriptionsState.length === 0
                      ? 'text-accent-gray hover:text-accent-gray cursor-not-allowed'
                      : ''
                      }`}
                    style={{
                      borderColor: theme.palette.secondary.v4
                    }}
                    disabled={currentPage === 1 || sortedJobDescriptionsState.length === 0}
                  >
                    <ChevronLeftIcon aria-hidden="true" className="h-5 w-5 text-white-400" />
                    <span className="hidden xs:inline ml-1">Prev</span>
                  </button>

                  <PaginationButtons
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                    theme={theme}
                  />

                  <button
                    onClick={() => handlePageChange(currentPage < totalPages ? currentPage + 1 : totalPages)}
                    className={`relative inline-flex items-center rounded px-2 py-2 text-white-400 bg-white hover:bg-accent-blue hover:text-white z-10 focus:outline-offset-0 text-xs sm:text-sm border flex-shrink-0 ${currentPage === totalPages || sortedJobDescriptionsState.length === 0
                      ? 'text-accent-gray hover:text-accent-gray cursor-not-allowed'
                      : ''
                      }`}
                    disabled={currentPage === totalPages || sortedJobDescriptionsState.length === 0}
                    style={{
                      borderColor: theme.palette.secondary.v4
                    }}
                  >
                    <span className="hidden xs:inline mr-1">Next</span>
                    <ChevronRightIcon aria-hidden="true" className="h-5 w-5 text-white-400" />
                  </button>
                </nav>
              </div>
            </div>
          )}
        </>
      )}
      {
        isModalOpen &&
        <AddJobModal
          totalJobs={originalJobDescriptions}
          setIsAddJobOpen={setIsModalOpen}
          isAddJobOpen={isModalOpen}
        />
      }
    </div>
  );
};

export default JobDescriptionList;
